import type {
	AbstractSearchFormProps,
	AbstractSearchFormState,
} from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { AbstractSearchForm } from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { Config } from "@config";
import { faCreditCard as farCreditCard } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faCalendarDays as fasCalendarDays,
	faBriefcase as fasBriefcase,
	faLock as fasLock,
	faArrowDownWideShort as fasArrowDownWideShort,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { Alert, CollapseBlock, FormCheck, FormCurrency, FormDate, FormNumber, FormSelect, FormSwitch } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Col, Row } from "react-bootstrap";
import type { LoanGoal } from "../LeadForm/types";
import {
	EMPLOYMENT_MONTHS_DATA_LIST,
	INCOME_DATA_LIST,
	LOAN_AMOUNT_DATA_LIST,
	LOAN_GOALS,
	MAX_EMPLOYMENT_MONTHS,
	MAX_LOAN_AMOUNT,
	MAX_MATURITY,
	MAX_TOTAL_INCOME,
	MIN_LOAN_AMOUNT,
	MIN_MATURITY,
	SORT_OPTIONS,
} from "./data";
import type { PersonalLoanCalculatorVariant, PersonalLoanSearchParams } from "./types";

interface Props extends AbstractSearchFormProps<PersonalLoanSearchParams> {
	loanGoal?: LoanGoal;
	hideLoanGoal?: boolean;
	variant?: PersonalLoanCalculatorVariant;
}

interface State extends AbstractSearchFormState {
	customFieldsCollapse: boolean;
}

export class PersonalLoanSearchForm extends AbstractSearchForm<PersonalLoanSearchParams, Props, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			customFieldsCollapse: false,
		};
	}

	protected renderFields(_values: PersonalLoanSearchParams): ReactNode {
		const { loanGoal, hideLoanGoal = false, highlightedBank, variant } = this.props;
		const { customFieldsCollapse } = this.state;
		const customColsProps = {
			lg: 4,
			sm: 6,
		};

		return (
			<>
				{highlightedBank === "sberbank" && (
					<Alert variant="warning" icon="auto">
						A termék jelenleg nem elérhető, igénylésed az alábbi oldalunkon tudod leadni:{" "}
						<a href="/szemelyi-kolcson/">Személyi kölcsön kalkulátor</a>
					</Alert>
				)}
				<Row className="justify-content-center">
					<Col xl={3} md={4} sm={6}>
						<FormCurrency
							label="Hitelösszeg"
							name="loanAmount"
							prepend={<FontAwesomeIcon icon={farCreditCard} />}
							validate={this.validateLoanAmount}
							dataList={LOAN_AMOUNT_DATA_LIST}
							placeholder="Add meg a hitelösszeget"
							required
						/>
					</Col>
					<Col xl={3} lg={4} md={3} sm={6}>
						<FormNumber
							label="Futamidő"
							name="maturityYears"
							prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
							infoTooltip="Mennyi ideig szeretnéd törleszteni a hitelt?"
							append="év"
							validate={this.validateMaturityYears}
							min={MIN_MATURITY}
							max={MAX_MATURITY}
							step={1}
							required
						/>
					</Col>
					<Col xl={3} lg={4} md={5} sm={6} xs={12}>
						<FormCurrency
							label="Havi nettó jövedelem"
							name="income"
							infoTooltip="Mekkora jövedelem érkezik havonta a bankszámládra?"
							validate={this.validateIncome}
							dataList={INCOME_DATA_LIST}
							placeholder="Add meg a jövedelmed"
							required
						/>
					</Col>
				</Row>
				<CollapseBlock
					title="Részletes kereső"
					name="custom_fields_collapse"
					variant="center"
					active={customFieldsCollapse}
					onClick={() => this.onCollapseClick()}
				>
					<Row className="justify-content-center">
						<Col xl={9}>
							<Row>
								<Col {...customColsProps}>
									<FormNumber
										label="Munkaviszony hossza"
										name="employmentMonths"
										infoTooltip="Mennyi ideje dolgozol jelenlegi munkahelyeden?"
										prepend={<FontAwesomeIcon icon={fasBriefcase} />}
										append="hónap"
										validate={this.validateEmploymentMonths}
										dataList={EMPLOYMENT_MONTHS_DATA_LIST}
										required
									/>
								</Col>

								{!hideLoanGoal && variant !== "CREDIT_EXCHANGE" && (
									<Col {...customColsProps}>
										<FormSelect
											label="Hitelcél"
											name="loanGoal"
											placeholder="Válassz ki egy hitelcélt!"
											infoTooltip="Mire szeretnéd felhasználni a hitelösszeget?"
											prepend={<FontAwesomeIcon icon={fasLock} />}
											options={LOAN_GOALS}
											readOnly={loanGoal !== undefined}
											required
										/>
									</Col>
								)}

								<Col {...customColsProps}>
									<FormSelect
										label="Hitelajánlatok rendezése"
										name="sort"
										prepend={<FontAwesomeIcon icon={fasArrowDownWideShort} />}
										options={SORT_OPTIONS}
										required
									/>
								</Col>

								<Col {...customColsProps}>
									<FormCheck className="mb-1" label="Online ügyintézés" name="fasterAdministration" />
									<FormCheck
										className="mb-1"
										label="Jövedelem érkeztetése"
										name="receiptOfIncome"
										infoTooltip="Vállalod, hogy a személyi kölcsönhöz tartozó törlesztési számlára rendszeres jövedelem jóváírás érkezik."
									/>
									<FormCheck
										className="mb-1"
										label="Aktív bankkapcsolat"
										name="activeBankContact"
										infoTooltip="Vállalod, hogy a személyi kölcsönhöz tartozó törlesztési számlán különböző megbízások teljesülnek (például: csoportos beszedési megbízás, stb.)"
									/>
								</Col>

								{!highlightedBank && (
									<Col {...customColsProps}>
										<FormSwitch
											className="mt-3 mt-md-0"
											label="Bankonként egy ajánlat"
											name="onePerBank"
										/>
									</Col>
								)}

								{this.hasExtra() && (
									<Col {...customColsProps}>
										<FormDate
											className="admin-only"
											label={
												<>
													<FontAwesomeIcon icon={fasLock} />
													Kalkuláció időpontja
												</>
											}
											name="calculationDate"
											required
										/>
									</Col>
								)}
							</Row>
						</Col>
					</Row>
				</CollapseBlock>
			</>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	@autobind
	private validateMaturityYears(maturityYears: number, values: PersonalLoanSearchParams): string {
		if (isNaN(maturityYears)) {
			return "A futamidő megadása kötelező!";
		}

		if (maturityYears < MIN_MATURITY) {
			return `A minimálisan megadható futamidő ${MIN_MATURITY} év!`;
		}

		if (maturityYears > MAX_MATURITY) {
			return `A maximálisan megadható futamidő ${MAX_MATURITY} év!`;
		}

		if (
			[
				"SZABAD_FELHASZNALAS",
				"GEPJARMU_VASARLAS",
				"SAJAT_BANKI_HITELKIVALTAS",
				"IDEGEN_BANKI_HITELKIVALTAS",
				"ENERGETIKAI_KORSZERUSITES",
			].includes(values.loanGoal) &&
			maturityYears > 8
		) {
			return "Csökkentsd a futamidőt, vagy válassz másik hitelcélt.";
		}
	}

	@autobind
	private validateEmploymentMonths(employmentMonths: number): string {
		if (isNaN(employmentMonths)) {
			return "A munkaviszony hosszának megadása kötelező!";
		}

		if (employmentMonths < 0) {
			return "A munkaviszony hossza nem lehet negatív szám!";
		}

		if (employmentMonths > MAX_EMPLOYMENT_MONTHS) {
			return `A megadható maximális munkaviszony ${MAX_EMPLOYMENT_MONTHS} hónap!`;
		}
	}

	@autobind
	private validateIncome(income: number): string {
		if (isNaN(income)) {
			return "A jövedelem megadása kötelező!";
		}

		if (income < Config.getMinWage()) {
			return `A megadható minimális összjövedelem ${formatText(
				Config.getMinWage(),
				"ft",
			)}, amely megegyezik az aktuális minimálbérrel.`;
		}

		if (income > MAX_TOTAL_INCOME) {
			return `A megadható maximális jövedelem ${formatText(MAX_TOTAL_INCOME, "ft")}!`;
		}
	}

	private onCollapseClick(): void {
		const { customFieldsCollapse } = this.state;

		this.setState({
			customFieldsCollapse: !customFieldsCollapse,
		});

		this.sendEvent("BM_advancedCalculation", { action: customFieldsCollapse ? "close" : "open" });
	}

	private hasExtra(): boolean {
		return Config.getCurrentUser().hasRole("PERSONAL_LOAN");
	}

	protected getComponentName(): string {
		return "PersonalLoanSearchForm";
	}
}
